import { ButtonSpinner, IconWithNameSpan, StyledButtonT1, StyledIconButton } from "./Button.styles";
import { ICON_COMPONENT_MAP } from "../../../constant/data";
import { useCallback } from "react";

/**
 * <ButtonT1>
 *
 * Reusable styled button with support for disabled state, loading state, and
 * accessible aria attributes. Displays a spinner and loading text when busy.
 *
 * Props
 * - title (string): main button label
 * - disabled (boolean): whether the button is disabled (default: false)
 * - loading (boolean): whether the button is in a loading state (default: false)
 * - loadingText (string): optional custom text to show while loading
 * - onClickCallback (Function): handler called on click if not disabled/loading
 * - ...rest (object): additional props passed to <StyledButtonT1>
 *
 * Returns
 * - ReactElement: styled button element with
 *     • Title text (or loading text)
 *     • Spinner icon when loading
 *     • Proper aria attributes for accessibility
 */

export const ButtonT1 = ({
  title,
  disabled = false,
  icon = "",
  loading = false,
  loadingText = "",
  onClickCallback = () => {},
  ...rest
}) => {
  const handleClick = useCallback(
    e => {
      if (!disabled && !loading) {
        onClickCallback(e);
      }
    },
    [disabled, loading, onClickCallback],
  );

  const isDisabled = disabled || loading;

  return (
    <StyledButtonT1
      {...rest}
      aria-busy={loading ? "true" : undefined}
      aria-live={loading ? "polite" : undefined}
      disabled={isDisabled}
      onClick={handleClick}
      $loading={loading}
    >
      {ICON_COMPONENT_MAP[icon] && <>{ICON_COMPONENT_MAP[icon]}</>}
      <span>{loading ? loadingText || "Loading…" : title}</span>
      {loading && <ButtonSpinner aria-hidden="true" />}
    </StyledButtonT1>
  );
};

export const IconButton = ({ onClick, icon: Icon, ...props }) => {
  return (
    <StyledIconButton onClick={onClick} {...props}>
      <Icon />
    </StyledIconButton>
  );
};

export const IconWithNameForButton = ({ icon: Icon, title }) => {
  return (
    <IconWithNameSpan>
      <Icon />
      {title}
    </IconWithNameSpan>
  );
};
